/**
 * Subtasks Tab Component
 *
 * PE-769: Displays and manages subtasks for a parent task.
 * Allows creating, completing, reopening, and deleting subtasks.
 */

import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getSubtasksForTask,
  getSubtaskStats,
  createSubtask,
  updateSubtask,
  completeSubtask,
  reopenSubtask,
  deleteSubtask,
  type Subtask,
} from '../../services/subtasksService';
import {
  Plus,
  CheckCircle2,
  Circle,
  Clock,
  User,
  Calendar,
  Trash2,
  Edit3,
  List,
  X,
  AlertCircle,
  PlayCircle,
} from 'lucide-react';
import { useToast } from '../../contexts/ToastContext';

interface SubtasksTabProps {
  /** Parent task ID */
  taskId: string;
  /** Parent task title for display */
  taskTitle?: string;
}

export const SubtasksTab: React.FC<SubtasksTabProps> = ({
  taskId,
  taskTitle,
}) => {
  const { success, error: showError } = useToast();
  const queryClient = useQueryClient();

  // State
  const [isCreating, setIsCreating] = useState(false);
  const [editingSubtaskId, setEditingSubtaskId] = useState<string | null>(null);
  const [newSubtask, setNewSubtask] = useState({
    title: '',
    description: '',
    dueDate: '',
    priority: 'Medium' as 'Low' | 'Medium' | 'High',
  });

  // Current user (mock - in real app would come from auth context)
  const currentUserId = 'user_1';
  const currentUserName = 'John Smith';

  // Load subtasks
  const { data: subtasks = [], isLoading, refetch } = useQuery({
    queryKey: ['subtasks', taskId],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 100));
      return getSubtasksForTask(taskId);
    },
  });

  // Calculate stats
  const stats = useMemo(() => getSubtaskStats(taskId), [taskId, subtasks]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: typeof newSubtask) => {
      await new Promise(resolve => setTimeout(resolve, 200));
      return createSubtask({
        parentTaskId: taskId,
        title: data.title,
        description: data.description || undefined,
        status: 'Open',
        priority: data.priority,
        dueDate: data.dueDate || undefined,
        createdBy: currentUserId,
        createdByName: currentUserName,
      });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['subtasks', taskId] });
      setIsCreating(false);
      setNewSubtask({
        title: '',
        description: '',
        dueDate: '',
        priority: 'Medium',
      });
      success('Subtask Created', `"${data.title}" has been added`);
    },
    onError: () => {
      showError('Error', 'Failed to create subtask');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<Subtask> }) => {
      await new Promise(resolve => setTimeout(resolve, 200));
      return updateSubtask(id, updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subtasks', taskId] });
      setEditingSubtaskId(null);
    },
    onError: () => {
      showError('Error', 'Failed to update subtask');
    },
  });

  // Complete mutation
  const completeMutation = useMutation({
    mutationFn: async (subtaskId: string) => {
      await new Promise(resolve => setTimeout(resolve, 200));
      return completeSubtask(subtaskId, currentUserId, currentUserName);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['subtasks', taskId] });
      if (data) {
        success('Subtask Completed', `"${data.title}" marked as complete`);
      }
    },
    onError: () => {
      showError('Error', 'Failed to complete subtask');
    },
  });

  // Reopen mutation
  const reopenMutation = useMutation({
    mutationFn: async (subtaskId: string) => {
      await new Promise(resolve => setTimeout(resolve, 200));
      return reopenSubtask(subtaskId);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['subtasks', taskId] });
      if (data) {
        success('Subtask Reopened', `"${data.title}" reopened`);
      }
    },
    onError: () => {
      showError('Error', 'Failed to reopen subtask');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: async (subtaskId: string) => {
      await new Promise(resolve => setTimeout(resolve, 200));
      return deleteSubtask(subtaskId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subtasks', taskId] });
      success('Subtask Deleted', 'Subtask has been removed');
    },
    onError: () => {
      showError('Error', 'Failed to delete subtask');
    },
  });

  // Handlers
  const handleCreateSubtask = useCallback(() => {
    if (!newSubtask.title.trim()) {
      showError('Validation Error', 'Subtask title is required');
      return;
    }
    createMutation.mutate(newSubtask);
  }, [newSubtask, createMutation, showError]);

  const handleToggleComplete = useCallback((subtask: Subtask) => {
    if (subtask.status === 'Completed') {
      reopenMutation.mutate(subtask.id);
    } else {
      completeMutation.mutate(subtask.id);
    }
  }, [completeMutation, reopenMutation]);

  const handleSetInProgress = useCallback((subtask: Subtask) => {
    updateMutation.mutate({
      id: subtask.id,
      updates: { status: 'In Progress' },
    });
  }, [updateMutation]);

  const handleDelete = useCallback((subtask: Subtask) => {
    if (window.confirm(`Delete subtask "${subtask.title}"?`)) {
      deleteMutation.mutate(subtask.id);
    }
  }, [deleteMutation]);

  const handleCancelCreate = useCallback(() => {
    setIsCreating(false);
    setNewSubtask({
      title: '',
      description: '',
      dueDate: '',
      priority: 'Medium',
    });
  }, []);

  // Helper functions
  const getPriorityColor = (priority?: string) => {
    switch (priority) {
      case 'High':
        return 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400';
      case 'Medium':
        return 'text-yellow-600 bg-yellow-50 dark:bg-yellow-900/20 dark:text-yellow-400';
      case 'Low':
        return 'text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400';
      default:
        return 'text-gray-600 bg-gray-100 dark:bg-gray-700';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Completed':
        return <CheckCircle2 className="w-5 h-5 text-green-600" />;
      case 'In Progress':
        return <PlayCircle className="w-5 h-5 text-blue-600" />;
      default:
        return <Circle className="w-5 h-5 text-gray-400 hover:text-blue-600" />;
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined,
    });
  };

  const isOverdue = (dueDate?: string) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date() && new Date(dueDate).toDateString() !== new Date().toDateString();
  };

  if (isLoading) {
    return (
      <div className="space-y-4 p-4">
        <div className="flex items-center justify-between">
          <div className="h-6 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
          <div className="h-9 w-28 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header with progress */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Subtasks ({stats.total})
          </h3>
          {stats.total > 0 && (
            <div className="flex items-center gap-3 mt-2">
              <div className="flex-1 max-w-[200px] h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full bg-green-500 transition-all duration-300"
                  style={{ width: `${stats.percentage}%` }}
                />
              </div>
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {stats.completed}/{stats.total} completed ({stats.percentage}%)
              </span>
            </div>
          )}
        </div>

        {!isCreating && (
          <button
            onClick={() => setIsCreating(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add Subtask
          </button>
        )}
      </div>

      {/* Create form */}
      {isCreating && (
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-blue-900 dark:text-blue-100">
              New Subtask
            </h4>
            <button
              onClick={handleCancelCreate}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-3">
            <div>
              <input
                type="text"
                placeholder="Subtask title *"
                value={newSubtask.title}
                onChange={(e) => setNewSubtask(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-3 py-2 bg-white dark:bg-[#0f172a] border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                autoFocus
              />
            </div>

            <div>
              <textarea
                placeholder="Description (optional)"
                value={newSubtask.description}
                onChange={(e) => setNewSubtask(prev => ({ ...prev, description: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 bg-white dark:bg-[#0f172a] border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Due Date
                </label>
                <input
                  type="date"
                  value={newSubtask.dueDate}
                  onChange={(e) => setNewSubtask(prev => ({ ...prev, dueDate: e.target.value }))}
                  className="w-full px-3 py-2 bg-white dark:bg-[#0f172a] border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Priority
                </label>
                <select
                  value={newSubtask.priority}
                  onChange={(e) => setNewSubtask(prev => ({ ...prev, priority: e.target.value as any }))}
                  className="w-full px-3 py-2 bg-white dark:bg-[#0f172a] border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="Low">Low</option>
                  <option value="Medium">Medium</option>
                  <option value="High">High</option>
                </select>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 pt-2">
            <button
              onClick={handleCreateSubtask}
              disabled={!newSubtask.title.trim() || createMutation.isPending}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Subtask'}
            </button>
            <button
              onClick={handleCancelCreate}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Subtasks list */}
      {subtasks.length === 0 && !isCreating ? (
        <div className="bg-white dark:bg-[#1e293b] rounded-lg border border-gray-200 dark:border-gray-700 p-12">
          <div className="text-center">
            <List className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
              No Subtasks
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Break down this task into smaller, manageable subtasks
            </p>
            <button
              onClick={() => setIsCreating(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Add First Subtask
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {subtasks.map((subtask) => (
            <div
              key={subtask.id}
              className={`bg-white dark:bg-[#1e293b] rounded-lg border transition-colors ${
                subtask.status === 'Completed'
                  ? 'border-gray-200 dark:border-gray-700 opacity-75'
                  : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700'
              }`}
            >
              <div className="p-4">
                <div className="flex items-start gap-3">
                  {/* Status toggle button */}
                  <button
                    onClick={() => handleToggleComplete(subtask)}
                    className="mt-0.5 flex-shrink-0 transition-transform hover:scale-110"
                    title={subtask.status === 'Completed' ? 'Reopen subtask' : 'Mark as complete'}
                  >
                    {getStatusIcon(subtask.status)}
                  </button>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1">
                        <h4
                          className={`font-medium ${
                            subtask.status === 'Completed'
                              ? 'text-gray-500 dark:text-gray-500 line-through'
                              : 'text-gray-900 dark:text-white'
                          }`}
                        >
                          {subtask.title}
                        </h4>
                        {subtask.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {subtask.description}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center gap-2">
                        {subtask.priority && (
                          <span
                            className={`px-2 py-0.5 text-xs font-medium rounded ${getPriorityColor(
                              subtask.priority
                            )}`}
                          >
                            {subtask.priority}
                          </span>
                        )}
                        {subtask.status === 'In Progress' && (
                          <span className="px-2 py-0.5 text-xs font-medium rounded bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400">
                            In Progress
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Meta info */}
                    <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500 dark:text-gray-400">
                      {subtask.dueDate && (
                        <div className={`flex items-center gap-1 ${
                          isOverdue(subtask.dueDate) && subtask.status !== 'Completed'
                            ? 'text-red-600 dark:text-red-400'
                            : ''
                        }`}>
                          <Calendar className="w-3 h-3" />
                          {isOverdue(subtask.dueDate) && subtask.status !== 'Completed' && (
                            <AlertCircle className="w-3 h-3" />
                          )}
                          Due {formatDate(subtask.dueDate)}
                        </div>
                      )}

                      {subtask.createdByName && (
                        <div className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          Created by {subtask.createdByName}
                        </div>
                      )}

                      {subtask.completedAt && subtask.completedByName && (
                        <div className="flex items-center gap-1 text-green-600 dark:text-green-400">
                          <CheckCircle2 className="w-3 h-3" />
                          Completed by {subtask.completedByName} on {formatDate(subtask.completedAt)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {subtask.status === 'Open' && (
                      <button
                        onClick={() => handleSetInProgress(subtask)}
                        className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 rounded transition-colors"
                        title="Start working"
                      >
                        <PlayCircle className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(subtask)}
                      className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded transition-colors"
                      title="Delete subtask"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stats summary at bottom if there are subtasks */}
      {stats.total > 0 && (
        <div className="flex items-center justify-center gap-6 pt-4 text-sm text-gray-500 dark:text-gray-400 border-t border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-1">
            <Circle className="w-4 h-4 text-gray-400" />
            {stats.open} Open
          </div>
          <div className="flex items-center gap-1">
            <PlayCircle className="w-4 h-4 text-blue-600" />
            {stats.inProgress} In Progress
          </div>
          <div className="flex items-center gap-1">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            {stats.completed} Completed
          </div>
        </div>
      )}
    </div>
  );
};

export default SubtasksTab;
